import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Calendar, Award, Eye,
  ChevronLeft, ChevronRight, Mic, BarChart2,
} from "lucide-react";
import api from "../../../utils/api";
import SpeakingResultModal from "../../../components/Ielts_test/hasil/speaking/ResultModal";

export default function SpeakingHistoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [history, setHistory] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, total: 0, per_page: 10 });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [questions, setQuestions] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [autoOpenedSessionId, setAutoOpenedSessionId] = useState(null);

  // Override body background to cream to prevent purple body bleed-through below footer
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "var(--st-color-cream)";
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const { data } = await api.get("/speaking/questions");
        if (data?.success) setQuestions(data.data);
      } catch (err) {
        console.error("Failed to load speaking questions:", err);
      }
    };
    loadQuestions();
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/speaking/results?page=${currentPage}&per_page=10`);
        if (data?.success) {
          setHistory(data.data);
          setMeta(data.meta || { current_page: currentPage, total: data.data.length, per_page: 10 });
        }
      } catch (err) {
        console.error("Failed to load speaking history:", err);
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, [currentPage]);

  const handleOpenDetails = useCallback(async (sessionId) => {
    if (modalLoading) return;
    setModalLoading(true);
    try {
      const { data } = await api.get(`/speaking/results/${sessionId}`);
      if (data?.success) {
        const resultDetail = data.data;
        const answersMap = {};

        if (Array.isArray(resultDetail.recordings)) {
          const availableRecordings = resultDetail.recordings.filter((rec) => rec.recording_id && !rec.is_expired);
          const token = localStorage.getItem("token");
          const audioEntries = await Promise.all(
            availableRecordings.map(async (rec) => {
              try {
                const streamPath = token
                  ? `/speaking/recordings/${rec.recording_id}/stream?token=${encodeURIComponent(token)}`
                  : `/speaking/recordings/${rec.recording_id}/stream`;

                const audioResponse = await api.get(streamPath, {
                  responseType: "blob",
                  skipAuthRedirect: true,
                });
                return [
                  rec.question_id,
                  {
                    audioUrl: URL.createObjectURL(audioResponse.data),
                    mimeType: audioResponse.data?.type || `audio/${rec.audio_format || "webm"}`,
                  },
                ];
              } catch (audioErr) {
                console.error("Failed to load speaking audio:", audioErr);
                return null;
              }
            })
          );

          audioEntries.filter(Boolean).forEach(([questionId, answer]) => {
            answersMap[questionId] = answer;
          });
        }

        setSelectedResult(resultDetail);
        setSelectedAnswers(answersMap);
        setShowModal(true);
      }
    } catch (err) {
      console.error("Failed to fetch speaking details:", err);
      alert("Gagal memuat detail evaluasi. Silakan coba lagi.");
    } finally {
      setModalLoading(false);
    }
  }, [modalLoading]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const getBandColor = (band) => {
    if (band == null) return "text-gray-400";
    if (band >= 7) return "text-green-600";
    if (band >= 5.5) return "text-blue-700";
    if (band >= 4) return "text-yellow-600";
    return "text-red-500";
  };

  const getScoreItems = (item) => [
    { label: "Overall", value: item.overall_band, main: true },
    { label: "Fluency", value: item.fluency_band },
    { label: "Lexical", value: item.lexical_band },
    { label: "Grammar", value: item.grammar_band },
    { label: "Pronunciation", value: item.pronunciation_band },
  ];

  const totalPages = Math.ceil(meta.total / meta.per_page) || 1;

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || autoOpenedSessionId === sessionId) return;

    setAutoOpenedSessionId(sessionId);
    handleOpenDetails(sessionId);
  }, [searchParams, autoOpenedSessionId, handleOpenDetails]);

  const closeDetailModal = () => {
    Object.values(selectedAnswers).forEach((answer) => {
      if (answer?.audioUrl) URL.revokeObjectURL(answer.audioUrl);
    });
    setShowModal(false);
    setSelectedResult(null);
    setSelectedAnswers({});
    if (searchParams.has("session_id")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("session_id");
      setSearchParams(nextParams, { replace: true });
    }
  };

  return (
    <div className="st-surface-cream">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e3e7e] to-[#a51c30] px-4 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate("/IELTSTest")}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white transition flex-shrink-0"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center border border-white/20 flex-shrink-0">
              <Mic size={16} className="text-yellow-300" />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-bold font-poppins text-white uppercase tracking-wide leading-tight">
                Speaking History
              </h1>
              <p className="text-[11px] text-blue-200 font-poppins hidden sm:block">
                Riwayat attempt &amp; evaluasi AI IELTS Speaking
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-5">
        {loading ? (
          <div className="flex flex-col justify-center items-center py-14 bg-white rounded-2xl shadow-sm">
            <Loader2 className="animate-spin text-[#1e3e7e] mb-3" size={36} />
            <p className="text-sm font-poppins text-gray-500">Memuat riwayat test...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center flex flex-col items-center">
            <div className="w-14 h-14 rounded-full bg-blue-50 text-[#1e3e7e] flex items-center justify-center mb-3">
              <Award size={28} />
            </div>
            <h3 className="font-poppins font-bold text-gray-800 text-base mb-1">Belum Ada Riwayat Test</h3>
            <p className="text-sm text-gray-500 font-poppins max-w-xs mb-5">
              Kamu belum menyelesaikan simulasi Speaking Test. Mulailah test pertamamu sekarang!
            </p>
            <button
              onClick={() => navigate("/speaking-test")}
              className="px-6 py-2.5 bg-[#1e3e7e] hover:bg-blue-800 text-white font-poppins font-bold rounded-full shadow-md transition text-sm"
            >
              Mulai Test
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* DESKTOP LIST */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
              <div className="bg-[#1e3e7e] text-white px-5 py-3 flex items-center justify-between">
                <div>
                  <h2 className="font-poppins font-bold text-sm uppercase tracking-wide">Riwayat Speaking</h2>
                  <p className="font-poppins text-[11px] text-blue-100">Band score per attempt dan detail evaluasi AI</p>
                </div>
                <span className="text-[11px] font-semibold bg-white/10 border border-white/15 px-3 py-1 rounded-full">
                  {meta.total} sesi
                </span>
              </div>

              <div className="max-h-[580px] overflow-y-auto divide-y divide-gray-100">
                {history.map((item) => (
                  <div key={item.session_id} className="grid grid-cols-[minmax(190px,1fr)_minmax(360px,1.65fr)_150px] gap-5 px-5 py-4 items-center hover:bg-gray-50/70 transition">
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-800 text-sm flex items-center gap-1.5 font-poppins mb-2">
                        <Calendar size={14} className="text-[#1e3e7e] flex-shrink-0" />
                        <span className="truncate">{formatDate(item.submitted_at)}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-[#1e3e7e] text-xs font-semibold border border-blue-100">
                          Attempt {item.attempt_number}
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${item.has_audio ? "bg-green-50 text-green-700 border-green-100" : "bg-gray-50 text-gray-400 border-gray-100"}`}>
                          Audio {item.has_audio ? "tersedia" : "expired"}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-5 gap-2">
                      {getScoreItems(item).map(({ label, value, main }) => (
                        <div key={label} className={`min-h-[58px] rounded-xl border px-2 py-2 text-center ${main ? "bg-[#1e3e7e]/5 border-[#1e3e7e]/20" : "bg-gray-50 border-gray-100"}`}>
                          <div className="text-[10px] text-gray-400 font-poppins mb-1 leading-tight truncate">{label}</div>
                          <div className={`font-black font-poppins text-lg leading-none ${getBandColor(value)}`}>
                            {value ?? "-"}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={() => handleOpenDetails(item.session_id)}
                        disabled={modalLoading}
                        className="inline-flex items-center justify-center gap-1.5 w-full max-w-[132px] px-3.5 py-2 bg-blue-50 hover:bg-[#1e3e7e] hover:text-white text-[#1e3e7e] rounded-xl border border-blue-200 hover:border-transparent transition text-xs font-semibold cursor-pointer disabled:opacity-50"
                      >
                        <Eye size={14} /> Detail
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="bg-gray-50 border-t border-gray-100 px-5 py-3 flex items-center justify-between font-poppins text-sm text-gray-500">
                  <span>
                    <span className="font-semibold">{history.length}</span> dari{" "}
                    <span className="font-semibold">{meta.total}</span> attempt
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer">
                      <ChevronLeft size={16} />
                    </button>
                    <span className="font-semibold text-gray-700 text-xs">{currentPage} / {totalPages}</span>
                    <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* MOBILE / TABLET CARDS */}
            <div className="md:hidden space-y-3">
              {history.map((item) => {
                return (
                  <div key={item.session_id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="font-bold text-gray-800 font-poppins text-sm flex items-center gap-1.5">
                        <Calendar size={13} className="text-[#1e3e7e]" /> {formatDate(item.submitted_at)}
                      </div>
                      <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-[#1e3e7e] text-[11px] font-semibold border border-blue-100 font-poppins flex-shrink-0">
                        Attempt {item.attempt_number}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 mb-3">
                      {getScoreItems(item).map(({ label, value, main }) => (
                        <div key={label} className={`min-h-[52px] rounded-xl p-2 text-center border ${main ? "bg-[#1e3e7e]/5 border-[#1e3e7e]/20" : "bg-gray-50 border-gray-100"}`}>
                          <div className="text-[9px] text-gray-400 font-poppins mb-0.5 leading-tight">{label}</div>
                          <div className={`font-black font-poppins text-sm leading-none ${getBandColor(value)}`}>
                            {value ?? "-"}
                          </div>
                        </div>
                      ))}
                      <div className="min-h-[52px] rounded-xl p-2 text-center border bg-gray-50 border-gray-100">
                        <div className="text-[9px] text-gray-400 font-poppins mb-0.5 leading-tight">Audio</div>
                        <div className={`font-black font-poppins text-xs leading-none ${item.has_audio ? "text-green-600" : "text-gray-400"}`}>
                          {item.has_audio ? "Ready" : "Expired"}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleOpenDetails(item.session_id)}
                      disabled={modalLoading}
                      className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-blue-50 hover:bg-[#1e3e7e] hover:text-white text-[#1e3e7e] border border-blue-200 hover:border-transparent transition text-xs font-semibold font-poppins cursor-pointer disabled:opacity-50"
                    >
                      <Eye size={13} /> Lihat Detail Sesi
                    </button>
                  </div>
                );
              })}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-1 font-poppins">
                  <span className="text-xs text-gray-500">
                    <span className="font-semibold">{history.length}</span> / <span className="font-semibold">{meta.total}</span> attempt
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 transition">
                      <ChevronLeft size={15} />
                    </button>
                    <span className="text-xs font-semibold text-gray-700">{currentPage} / {totalPages}</span>
                    <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 transition">
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="flex items-center gap-2 px-1 pt-1">
              <BarChart2 size={13} className="text-gray-400" />
              <p className="text-xs text-gray-400 font-poppins">
                Total <span className="font-semibold text-gray-600">{meta.total}</span> sesi speaking tercatat
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {modalLoading && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 shadow-2xl flex flex-col items-center max-w-xs w-full mx-4 text-center border border-gray-100">
            <Loader2 className="animate-spin text-[#1e3e7e] mb-3" size={32} />
            <h4 className="font-poppins font-bold text-gray-800 text-sm mb-1">Memuat Detail Evaluasi...</h4>
            <p className="text-xs text-gray-400 font-poppins">Silakan tunggu sebentar.</p>
          </div>
        </div>
      )}

      {/* Detail Session Modal */}
      {showModal && selectedResult && (
        <SpeakingResultModal
          answers={selectedAnswers}
          evaluationResult={selectedResult}
          questions={questions}
          onClose={closeDetailModal}
        />
      )}
    </div>
  );
}
